import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { customInstance } from "@/lib/axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

interface Lender {
  id: number;
  name: string;
  email: string;
  is_active: boolean;
}

export function AdminLendersPage() {
  const queryClient = useQueryClient();

  const { data: lenders, isLoading } = useQuery({
    queryKey: ["lenders"],
    queryFn: async () => {
      const { data } = await customInstance.get<{ results: Lender[] }>("/api/lenders/");
      return data.results;
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: number; isActive: boolean }) => {
      await customInstance.patch(`/api/lenders/${id}/`, { is_active: !isActive });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lenders"] });
      toast.success("Lender updated");
    },
  });

  if (isLoading) return <div>Loading...</div>;

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Manage Lenders</h1>
          <p className="mt-2 text-sm text-gray-600">Configure lending partners</p>
        </div>
        <Button className="w-full sm:w-auto">+ Add Lender</Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Lenders</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px]">
            <thead className="bg-blue-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {lenders?.map((lender) => (
                <tr key={lender.id}>
                  <td className="px-6 py-4 text-sm font-medium">{lender.name}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{lender.email}</td>
                  <td className="px-6 py-4">
                    <Badge variant={lender.is_active ? "default" : "secondary"}>
                      {lender.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </td>
                  <td className="px-6 py-4">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => toggleActiveMutation.mutate({ id: lender.id, isActive: lender.is_active })}
                    >
                      {lender.is_active ? "Deactivate" : "Activate"}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}







